/** @jsx jsx */
import React, { useMemo, useCallback, useEffect } from 'react';
import { jsx } from '@emotion/core';
import { conformToMask, decimalMoneyMask } from 'utils/masks';
import useTranslate, {
  useMarkdownTranslate,
} from 'hooks/useTranslate/useTranslate';
import styles from '../styles';
import { SplitPayOffer } from 'state/financeOffers';
import { addDays, format } from 'date-fns/fp';

import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import FiberManualRecordOutlinedIcon from '@material-ui/icons/FiberManualRecordOutlined';
import Link from 'components/Link';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import {
  Box,
  Stepper,
  StepConnector,
  Step,
  StepLabel,
  Divider,
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { helpText } from 'features/Theme';
import ExpandablePanel from 'components/ExpandablePanel';
import { map, range } from 'ramda';
import Button from 'components/Button';

import useNavigationHelpers from 'hooks/useNavigationHelpers';
import ActionButtonsContainer from 'components/ActionButtonsContainer';
import { mergeApplication } from 'state/creditApplication';

import useAnalytics, { Event } from 'hooks/useAnalytics';
import useAnalyticVariables from 'hooks/useAnalyticVariables';
import BreadTitle from 'features/unified/OfferIntro/BreadTitle';

interface SplitPayOfferData {
  id: string;
  capacityValue: number;
  biweeklyPayment: number;
  termLength: number;
}

export interface SplitPayPaymentOptionProps {
  intendedPurchaseAmount: number;
  splitPayOffer: SplitPayOffer;
  expanded: boolean;
  panelName: string;
  onChange: React.ComponentProps<typeof ExpandablePanel>['onChange'];
}

const getSplitPayOfferData = (
  principal: number,
  offer: SplitPayOffer,
): SplitPayOfferData => {
  const termLength = offer.termLength;
  const biweeklyPayment = principal / termLength;
  return {
    id: offer.id,
    capacityValue: offer.capacityValue,
    biweeklyPayment,
    termLength,
  };
};

const getDateLabelsFromToday = (period: number, numberOfPeriods: number) => {
  const today = new Date();
  const dateLabels = map(i => {
    const date = addDays(period * i, today);
    return {
      label: format('MMM dd, yyyy', date),
    };
  }, range(1, numberOfPeriods));

  return [
    {
      label: 'Today',
    },
    ...dateLabels,
  ];
};

const StepperIcon: React.FC<{ active: boolean }> = props => {
  return props.active ? (
    <FiberManualRecordIcon />
  ) : (
    <FiberManualRecordOutlinedIcon />
  );
};

const SplitPayCheckout: React.FC<SplitPayPaymentOptionProps> = props => {
  const {
    intendedPurchaseAmount,
    panelName,
    splitPayOffer,
    expanded,
    onChange,
  } = props;

  const splitPayOfferData = useMemo(
    () => getSplitPayOfferData(intendedPurchaseAmount, splitPayOffer),
    [intendedPurchaseAmount, splitPayOffer],
  );

  const steps = useMemo(
    () =>
      getDateLabelsFromToday(
        14, // BIWEEKLY
        splitPayOffer.termLength,
      ),
    [splitPayOffer.termLength],
  );

  const { push } = useNavigationHelpers();
  const dispatch = useDispatch();

  const nextPath = useCallback(() => {
    dispatch(mergeApplication({ selectedOfferId: splitPayOffer.id }));
    push(`/unified/checkout/splitpay/place-order/${splitPayOffer.id}`);
  }, [dispatch, push, splitPayOffer.id]);

  const { sendEvents } = useAnalytics();
  const { setAnalyticsVariables } = useAnalyticVariables();

  useEffect(() => {
    expanded && sendEvents([Event.SplitpayOfferExpanded]);
  }, [expanded, sendEvents, setAnalyticsVariables]);

  const t = useTranslate();
  const tMd = useMarkdownTranslate();
  const title = (
    <div>
      <div css={styles.paymentOption.summaryTitle}>
        <BreadTitle
          titlePart={tMd('unified.offer.paymentOption.splitPay.title')}
          logo={t('unified.offer.paymentOption.splitPay.breadLogo')}
          id={'splitpayBreadLogo'}
        />
      </div>

      <div css={styles.paymentOption.summaryBody}>
        {/*4 interest-free payments of <span css={styles.boldColorText}>$150</span>{' '}
        every 2 weeks*/}
        {tMd('unified.offer.paymentOption.splitPay.description', {
          amount: conformToMask(
            splitPayOfferData.biweeklyPayment.toFixed(2),
            decimalMoneyMask,
          ),
          termLength: splitPayOfferData.termLength,
        })}
      </div>
    </div>
  );

  const stepConnector = <StepConnector css={styles.stepConnector} />;

  const body = (
    <div css={styles.paymentOption.body}>
      <div css={styles.paymentOption.section}>
        <Divider />
      </div>
      <div>
        <span css={helpText}>
          {tMd('unified.offer.paymentOption.splitPay.taxShippingCheckout')}
        </span>
      </div>
      {/* See material ui steppers https://mui.com/components/steppers/ */}
      <Box>
        <Stepper
          activeStep={0}
          orientation="vertical"
          connector={stepConnector}
          css={styles.stepper}
        >
          {steps.map(step => (
            <Step key={step.label}>
              <StepLabel
                css={styles.stepCircle}
                StepIconComponent={StepperIcon}
              >
                {step.label}{' '}
                <span css={{ float: 'right' }}>
                  <strong css={styles.stepPrice}>
                    {conformToMask(
                      splitPayOfferData.biweeklyPayment.toFixed(2),
                      decimalMoneyMask,
                    )}
                  </strong>
                </span>
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Link
          id="splitPayLearnMore"
          to={`/unified/checkout-offer/splitpay/learn-more/${splitPayOfferData.id}`}
          css={styles.chevronRightIcon}
        >
          {tMd('unified.offer.paymentOption.splitPay.learnMoreButton')}
          <ChevronRightIcon />
        </Link>
      </Box>
    </div>
  );
  const footer = (
    <div css={styles.installmentFooter}>
      <Box mb={2}>
        <ActionButtonsContainer>
          <Button
            id={'splitpay-accept-button'}
            mobileFullWidth={true}
            onClick={nextPath}
          >
            {tMd('unified.offer.paymentOption.splitPay.continue')}
          </Button>
        </ActionButtonsContainer>
      </Box>
    </div>
  );
  return (
    <ExpandablePanel
      expanded={expanded}
      title={title}
      children={body}
      panelName={panelName} // FIXME: do this in the upper level?
      onChange={onChange}
      banner={tMd('unified.offer.paymentOption.splitPay.banner', {
        amount: conformToMask(
          (splitPayOfferData.capacityValue / 100).toFixed(2),
          decimalMoneyMask,
        ),
      })}
      footer={footer}
    />
  );
};
export default SplitPayCheckout;
